import { generateClient } from 'aws-amplify/api';
import type {
  AdmissionsMutationArgsMap,
  AdmissionsMutationOperationId,
  AdmissionsMutationResultMap,
  AdmissionsQueryArgsMap,
  AdmissionsQueryOperationId,
  AdmissionsQueryResultMap,
} from '@triedge/admissions-domain';
import type {
  DomainRouterTransportEnvelope,
  DomainRouterTransportRequest,
} from '@triedge/domain-core';
import {
  serializeDomainRouterTransportInput,
} from '@triedge/domain-core';
import type {
  FinancialAidQueryArgsMap,
  FinancialAidQueryOperationId,
  FinancialAidQueryResultMap,
} from '@triedge/financial-aid-domain';
import type {
  StaffSettingsMutationArgsMap,
  StaffSettingsMutationOperationId,
  StaffSettingsMutationResultMap,
  StaffSettingsQueryArgsMap,
  StaffSettingsQueryOperationId,
  StaffSettingsQueryResultMap,
} from '@triedge/staff-settings-domain';

type GraphQLError = {
  message?: string;
};

type GraphqlResponse<TData> = {
  data?: TData;
  errors?: GraphQLError[];
};

type TransportFieldKind = 'query' | 'mutation';
type JsonRecord = Record<string, unknown>;

const TRANSPORT_FIELD_BY_KIND = {
  query: {
    admissions: 'domainAdmissionsQuery',
    financialAid: 'domainFinancialAidQuery',
    settings: 'domainSettingsQuery',
  },
  mutation: {
    admissions: 'domainAdmissionsMutation',
    settings: 'domainSettingsMutation',
  },
} as const;

const buildTransportDocument = (kind: TransportFieldKind, fieldName: string): string => /* GraphQL */ `
  ${kind} DomainRouterTransport($operationId: String!, $input: AWSJSON) {
    ${fieldName}(operationId: $operationId, input: $input) {
      operationId
      payload
    }
  }
`;

const parsePayload = <TPayload>(payload: unknown): TPayload => {
  if (typeof payload !== 'string') {
    return payload as TPayload;
  }
  try {
    return JSON.parse(payload) as TPayload;
  } catch {
    return payload as TPayload;
  }
};

export const normalizeTransportPayload = (value: unknown): JsonRecord => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const record = value as JsonRecord;
  const keys = Object.keys(record);
  if (keys.length === 1 && keys[0] === 'input') {
    const nested = record.input;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as JsonRecord;
    }
  }

  return record;
};

const unwrapTransportResponse = <TPayload>(
  fieldName: string,
  response: GraphqlResponse<Record<string, DomainRouterTransportEnvelope<unknown>>>
): TPayload => {
  if (response.errors?.length) {
    throw new Error(response.errors.map((error) => error.message ?? 'Unknown GraphQL error').join('; '));
  }
  const envelope = response.data?.[fieldName];
  if (!envelope) {
    throw new Error(`Missing ${fieldName} transport envelope.`);
  }
  return parsePayload<TPayload>(envelope.payload);
};

const executeTransport = async <TPayload>(
  kind: TransportFieldKind,
  fieldName: string,
  input: DomainRouterTransportRequest<unknown>
): Promise<TPayload> => {
  const client = generateClient();
  const response = await client.graphql({
    query: buildTransportDocument(kind, fieldName),
    variables: {
      operationId: input.operationId,
      input: serializeDomainRouterTransportInput(normalizeTransportPayload(input.input)),
    },
  }) as GraphqlResponse<Record<string, DomainRouterTransportEnvelope<unknown>>>;
  return unwrapTransportResponse<TPayload>(fieldName, response);
};

export const callAdmissionsTransportQuery = <
  TOperationId extends AdmissionsQueryOperationId,
>(
  operationId: TOperationId,
  input: AdmissionsQueryArgsMap[TOperationId]
): Promise<AdmissionsQueryResultMap[TOperationId]> => (
  executeTransport<AdmissionsQueryResultMap[TOperationId]>(
    'query',
    TRANSPORT_FIELD_BY_KIND.query.admissions,
    {
      operationId,
      input,
    }
  )
);

export const callAdmissionsTransportMutation = <
  TOperationId extends AdmissionsMutationOperationId,
>(
  operationId: TOperationId,
  input: AdmissionsMutationArgsMap[TOperationId]
): Promise<AdmissionsMutationResultMap[TOperationId]> => (
  executeTransport<AdmissionsMutationResultMap[TOperationId]>(
    'mutation',
    TRANSPORT_FIELD_BY_KIND.mutation.admissions,
    {
      operationId,
      input,
    }
  )
);

export const callFinancialAidTransportQuery = <
  TOperationId extends FinancialAidQueryOperationId,
>(
  operationId: TOperationId,
  input: FinancialAidQueryArgsMap[TOperationId]
): Promise<FinancialAidQueryResultMap[TOperationId]> => (
  executeTransport<FinancialAidQueryResultMap[TOperationId]>(
    'query',
    TRANSPORT_FIELD_BY_KIND.query.financialAid,
    {
      operationId,
      input,
    }
  )
);

export const callSettingsTransportQuery = <
  TOperationId extends StaffSettingsQueryOperationId,
>(
  operationId: TOperationId,
  input: StaffSettingsQueryArgsMap[TOperationId]
): Promise<StaffSettingsQueryResultMap[TOperationId]> => (
  executeTransport<StaffSettingsQueryResultMap[TOperationId]>(
    'query',
    TRANSPORT_FIELD_BY_KIND.query.settings,
    {
      operationId,
      input,
    }
  )
);

export const callSettingsTransportMutation = <
  TOperationId extends StaffSettingsMutationOperationId,
>(
  operationId: TOperationId,
  input: StaffSettingsMutationArgsMap[TOperationId]
): Promise<StaffSettingsMutationResultMap[TOperationId]> => (
  executeTransport<StaffSettingsMutationResultMap[TOperationId]>(
    'mutation',
    TRANSPORT_FIELD_BY_KIND.mutation.settings,
    {
      operationId,
      input,
    }
  )
);
